import React, { useEffect } from 'react';

import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
  Tooltip,
} from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import { useTranslation } from 'react-i18next';
import styles from './styles';
import { RootStateOrAny, useDispatch, useSelector } from 'react-redux';
import AppConstants from '../../AppConstants';
import { paginationCountLabel } from '../../utils/helpers';
import {
  getReport,
  setViewReportPageAction,
  setViewReportRowsPerPageAction,
} from '../../redux/actions/uploadHistoryActions';

type ViewReportModalProps = {
  isOpen: boolean;
  onClose: () => void;
  reportData?: any;
};

export default function ViewReportModal({
  isOpen,
  onClose,
  reportData,
}: ViewReportModalProps) {
  const classes = styles();
  const { t } = useTranslation();
  const dispatch: any = useDispatch();

  const rows = useSelector(
    (state: RootStateOrAny) => state?.UploadHistoryReducer?.report,
  );

  const paginationConfig = useSelector(
    (state: RootStateOrAny) =>
      state?.UploadHistoryReducer?.viewReportPaginationConfig,
  );

  useEffect(() => {
    dispatch(
      getReport(reportData?.id, { PAGE: 1, LIMIT: AppConstants.rowsPerPage }),
    );
    dispatch(setViewReportRowsPerPageAction(AppConstants.rowsPerPage));
    dispatch(setViewReportPageAction(1));
  }, []);

  if (rows.length) {
    rows.sort((a, b) => {
      return a.row_Id - b.row_Id;
    });
  }
  const tableDescriptionMessage = (row, key) => {
    if (!row[key] || row[key].includes('null')) return '-';
    const statusDesc = row[key]
      .split(',')
      .map(message => {
        return message && t(`massUpload.history.${message}`);
      })
      .filter(message => message && !message.includes('null'))
      .join(',');
    return (
      <Tooltip title={statusDesc}>
        <div
          style={
            statusDesc.length > 20 ? { width: '40ch' } : { width: '120px' }
          }
          className={classes.cellWrapper}
        >
          {statusDesc}
        </div>
      </Tooltip>
    );
  };
  interface Column {
    id:
      | 'row_Id'
      | 'updated_restaurants_numbers'
      | 'not_updated_restaurants_numbers'
      | 'status_description';
    label: string | any;
    minWidth?: number;
    maxWidth?: number;
    paddingTop?: number;
    paddingBottom?: number;
    paddingLeft?: number;
    paddingRight?: number;
    textAlign?: string | any;
    padding?: number;
    isFilterAvailable?: boolean;
    actions?: [any];
  }

  const columns: Column[] = [
    {
      minWidth: 180,
      maxWidth: 200,
      paddingTop: 0,
      paddingBottom: 0,
      paddingLeft: 0,
      paddingRight: 1,
      label: t('massUpload.history.updated_restaurants'),
      id: 'updated_restaurants_numbers',
    },
    {
      minWidth: 180,
      maxWidth: 200,
      paddingTop: 0,
      paddingBottom: 0,
      paddingLeft: 0,
      paddingRight: 1,
      label: t('massUpload.history.nonUpdated_restaurants'),
      id: 'not_updated_restaurants_numbers',
    },
    {
      minWidth: 160,
      maxWidth: 200,
      paddingTop: 0,
      paddingBottom: 0,
      paddingLeft: 0,
      paddingRight: 1,
      label: t('massUpload.history.description'),
      id: 'status_description',
      actions: [tableDescriptionMessage],
    },
  ];

  if (reportData?.upload_type === 'mass_upload') {
    columns.unshift({
      minWidth: 140,
      maxWidth: 160,
      paddingTop: 0,
      paddingBottom: 0,
      paddingLeft: 0,
      paddingRight: 1,
      label: t('operationHours.column.rowNumbers'),
      id: 'row_Id',
    });
  }

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    dispatch(setViewReportRowsPerPageAction(+event.target.value));
    dispatch(setViewReportPageAction(1));
    dispatch(
      getReport(reportData?.id, { PAGE: 1, LIMIT: +event.target.value }),
    );
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    dispatch(setViewReportPageAction(newPage + 1));
    dispatch(
      getReport(reportData?.id, {
        PAGE: newPage + 1,
        LIMIT: paginationConfig.rowsPerPage,
      }),
    );
  };

  return (
    <Dialog
      onClose={onClose}
      aria-labelledby="customized-dialog-title"
      className={classes.viewReportModal}
      open={isOpen}
      disableBackdropClick={true}
    >
      <DialogTitle id="customized-dialog-title">
        <Grid container justifyContent="space-between">
          <Grid item>
            <Typography variant="h6" style={{ fontWeight: 600 }}>
              {t('massUpload.history.viewReport')}
            </Typography>
          </Grid>
          <Grid item>
            <IconButton style={{ padding: 0 }} onClick={onClose}>
              <CloseIcon />
            </IconButton>
          </Grid>
        </Grid>
      </DialogTitle>
      <DialogContent className={`${classes.viewReportModalContent}`}>
        <Box marginTop={1}>
          <TableContainer className={classes.container}>
            <Table stickyHeader aria-label="sticky table" size="small">
              <TableHead>
                <TableRow>
                  {columns.map((column, index) => (
                    <TableCell
                      key={`column-${index}`}
                      style={{
                        minWidth: column.minWidth,
                        maxWidth: column.maxWidth,
                        paddingTop: column.paddingTop,
                        paddingBottom: column.paddingBottom,
                        paddingLeft: column.paddingLeft,
                        paddingRight: column.paddingRight,
                        fontSize: 16,
                      }}
                    >
                      <Box
                        style={{
                          textAlign: 'center',
                        }}
                        paddingY={1}
                        display="flex"
                        justifyContent="center"
                        alignContent="center"
                      >
                        <b>{column.label}</b>
                      </Box>
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.length ? (
                  rows.map(row => {
                    return (
                      (row['updated_restaurants_numbers'] ||
                        row['not_updated_restaurants_numbers']) && (
                        <TableRow
                          hover
                          role="checkbox"
                          tabIndex={-1}
                          key={row.id}
                        >
                          {columns.map((column: Column) => {
                            if (column.actions) {
                              return (
                                <TableCell key={column.id}>
                                  <Box
                                    display="flex"
                                    paddingY={1}
                                    justifyContent="center"
                                    alignContent="center"
                                  >
                                    {column.actions[0](row, column.id)}
                                  </Box>
                                </TableCell>
                              );
                            }
                            const value = row[column.id];
                            return (
                              <TableCell key={column.id}>
                                <Box
                                  display="flex"
                                  paddingY={1}
                                  justifyContent="center"
                                  alignContent="center"
                                >
                                  <Tooltip title={value && value}>
                                    <div
                                      style={
                                        value.length > 20
                                          ? { width: '40ch' }
                                          : { width: '100px' }
                                      }
                                      className={classes.cellWrapper}
                                    >
                                      {value ? value : '-'}
                                    </div>
                                  </Tooltip>
                                </Box>
                              </TableCell>
                            );
                          })}
                        </TableRow>
                      )
                    );
                  })
                ) : (
                  <TableRow>
                    <TableCell colSpan={42}>
                      <div style={{ textAlign: 'center' }}>
                        <Typography>
                          {t('massUpload.history.noHistory')}
                        </Typography>
                      </div>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            rowsPerPageOptions={AppConstants.rowsPerPageOptions}
            count={paginationConfig.total}
            rowsPerPage={paginationConfig.rowsPerPage}
            page={paginationConfig.page - 1}
            component={'div'}
            onPageChange={handleChangePage}
            onChangeRowsPerPage={handleChangeRowsPerPage}
            labelRowsPerPage={t('app.common.rowsPerPage')}
            labelDisplayedRows={({ from, to, count }) =>
              paginationCountLabel(
                from,
                to,
                count,
                t('app.common.paginationCountLabel'),
              )
            }
          />
        </Box>
      </DialogContent>
      <DialogActions className={classes.tabPanel}>
        <Button
          variant="contained"
          className={classes.viewReportModalButton}
          color="primary"
          autoFocus
          onClick={onClose}
        >
          {t('app.common.cancel')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
